// ---------------------------------------------------------------------------
// JobResultDialog — popup scorecard for a completed evaluation run.
//
// Reuses the server-rendered HTML report (metrics, confusion counts,
// drill-downs) fetched via EvaluationRunsService.getReportHtml and shown in
// a sandboxed iframe (same approach as EvalReportPage, but in a dialog).
// `srcDoc` is used so the authenticated client fetch — not the iframe — is
// what retrieves the HTML. ReportFrame keeps the script-only sandbox and owns
// validated trajectory navigation and its fallback link outside the report.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Dialog,
  DialogSurface,
  DialogTrigger,
  Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import { useReportTheme } from '../../../components/ReportFrame/useReportTheme.js';
import { useReportDownload } from '../../../features/report-download/useReportDownload.js';
import { ReportFrame } from '../../../features/report-trajectory/index.js';
import type { IEvaluationRunsService } from '../../../services/evaluationRunsService.interface.js';
import { TelemetryPanel } from '../../../telemetry/components/TelemetryPanel/TelemetryPanel.js';
import { PanelRegion, PanelType } from '../../../telemetry/userbi.js';

import { useStyles } from './JobResultDialog.style.js';

export interface JobResultDialogProps {
  workspaceName: string;
  /** The run whose results to show; `null` keeps the dialog closed. */
  runId: string | null;
  runsService: IEvaluationRunsService;
  onClose: () => void;
}

export function JobResultDialog({
  workspaceName,
  runId,
  runsService,
  onClose,
}: JobResultDialogProps) {
  const styles = useStyles();
  const [report, setReport] = useState<{
    html: string;
    runId: string;
    workspaceName: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const html =
    report?.runId === runId &&
    report?.workspaceName === workspaceName &&
    !loading &&
    error === null
      ? report.html
      : null;
  const embeddedHtml = useMemo(() => {
    if (html === null) return null;
    const document = new DOMParser().parseFromString(html, 'text/html');
    document
      .getElementById('aistudio-download-report')
      ?.removeAttribute('hidden');
    return `<!doctype html>\n${document.documentElement.outerHTML}`;
  }, [html]);
  const {
    frameRef: reportFrame,
    srcDoc,
    syncTheme,
  } = useReportTheme(embeddedHtml);
  const { error: downloadError } = useReportDownload({
    panelType: PanelType.EvaluationResultsDialog,
    iframeRef: reportFrame,
    html,
    runId,
  });

  useEffect(() => {
    if (runId === null) {
      setReport(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReport(null);
    runsService
      .getReportHtml(workspaceName, [runId])
      .then((reportHtml) => {
        if (!cancelled) setReport({ html: reportHtml, runId, workspaceName });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load results.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId, workspaceName, runsService]);

  return (
    <Dialog
      open={runId !== null}
      onOpenChange={(_e, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={styles.surface} aria-label="Evaluation results">
        <TelemetryPanel
          panelType={PanelType.EvaluationResultsDialog}
          panelRegion={PanelRegion.Overlay}
        >
          <div className={styles.header}>
            <div className={styles.titleBlock}>
              <span className={styles.title}>Evaluation results</span>
              {runId !== null && (
                <span className={styles.subtitle}>Run {runId}</span>
              )}
            </div>
            <DialogTrigger action="close" disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
              />
            </DialogTrigger>
          </div>

          {downloadError !== null && (
            <Body1 role="alert" className={styles.error}>
              {downloadError}
            </Body1>
          )}
          <div className={styles.body}>
            {loading ? (
              <div className={styles.centered}>
                <Spinner label="Loading results…" />
              </div>
            ) : error !== null ? (
              <Body1 role="alert" className={styles.error}>
                {error}
              </Body1>
            ) : html !== null ? (
              <ReportFrame
                key={`${workspaceName}/${runId}`}
                iframeRef={reportFrame}
                className={styles.iframe}
                title="Evaluation results"
                html={srcDoc ?? ''}
                onLoad={syncTheme}
              />
            ) : null}
          </div>
        </TelemetryPanel>
      </DialogSurface>
    </Dialog>
  );
}
